// Uptime on Overview: is the site up, how often it has been over 30 days, how fast it answers,
// and when its certificate runs out. Filled from /api/uptime after the rest of the screen.
import type { UptimeView } from "../src/alerts-types";
import { shared } from "./share";

type Api = <T>(path: string, p?: Record<string, string>) => Promise<T>;

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayLabel = (s: string) => `${Number(s.slice(8, 10))} ${MON[Number(s.slice(5, 7)) - 1]}`;
const DAY = 86_400_000;

/** "100%", "99.93%": two decimals below 100, never rounded up to a perfect score. */
export function fmtUptime(x: number | null): string {
  if (x === null) return "–";
  if (x >= 1) return "100%";
  return `${(Math.floor(x * 10000) / 100).toFixed(2)}%`;
}

/** Green, amber or red for a share of successful checks. */
export const uptimeTone = (x: number | null) => (x === null ? "none" : x >= 0.999 ? "good" : x >= 0.99 ? "ni" : "poor");

/** The card shell, with a slot `renderUptime` fills (or removes, for sites with no domain). */
export function uptimeCard(): string {
  if (shared) return "";
  return `<section class="card" id="uptime-card"><div class="card-head"><h2>Uptime</h2><span class="spacer"></span><span class="hint">Checked every 5 min</span></div><div id="uptime"><div class="skeleton" style="height:84px"></div></div></section>`;
}

const when = (ms: number, tz: string) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(ms);

export async function renderUptime(o: { api: Api; project: string; timezone: string; current: () => boolean }): Promise<void> {
  const host = document.getElementById("uptime");
  if (!host || shared) return;
  let u: UptimeView;
  try {
    u = await o.api<UptimeView>("uptime", { project: o.project });
  } catch {
    if (o.current()) document.getElementById("uptime-card")?.remove();
    return;
  }
  if (!o.current()) return;
  // Apps and sites without a domain have nothing to check.
  if (!u.domain) return void document.getElementById("uptime-card")?.remove();

  const head =
    u.status === "down"
      ? `<span class="pill poor">Down</span><span>${u.down_since ? `since ${when(u.down_since, o.timezone)}` : u.http_status ? `HTTP ${u.http_status}` : esc(u.error ?? "")}</span>`
      : u.status === "up"
        ? `<span class="pill good">Up</span><span>${u.ms !== null ? `answered in ${u.ms.toLocaleString("en-US")} ms` : ""}</span>`
        : `<span class="pill none">Not checked yet</span>`;
  const days = Math.round(((u.cert_expires_at ?? 0) - Date.now()) / DAY);
  const cert =
    u.cert_expires_at === null
      ? `<span class="muted">Not known yet</span>`
      : `<span class="${days <= 14 ? "up-warn" : ""}">Valid until ${new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(u.cert_expires_at)}</span> <span class="muted">${days <= 0 ? "expired" : `${days} ${days === 1 ? "day" : "days"} left`}</span>`;
  const bars = u.days
    .map((d) => `<i class="${uptimeTone(d.uptime)}" title="${dayLabel(d.day)}: ${d.uptime === null ? "no checks" : fmtUptime(d.uptime)}"></i>`)
    .join("");

  host.innerHTML = `<div class="up-card">
    <div class="up-head">${head}<span class="spacer"></span><span class="up-domain">${esc(u.domain)}</span></div>
    <div class="up-bars" role="img" aria-label="Uptime per day, last 30 days">${bars}</div>
    <div class="up-facts">
      <div><span class="k">Last 30 days</span><b class="num ${uptimeTone(u.uptime_30d)}">${fmtUptime(u.uptime_30d)}</b></div>
      <div><span class="k">Response, 24h avg</span><b class="num">${u.avg_ms_24h !== null ? `${u.avg_ms_24h.toLocaleString("en-US")} ms` : "–"}</b></div>
      <div><span class="k">Certificate</span><span class="v">${cert}</span></div>
    </div>
  </div>`;
}
